import AffiliateInfo from '@/components/affiliate/AffiliateInfo';
import { Panel } from '@/components/panel/panel';
import Money from '@/components/text/money';
import type { InvolvedAffiliate, Payout } from '@/types/payout';
import { Box, Flex, Stack, Table, Text } from '@chakra-ui/react';
import type { AffiliatePayoutSummary } from '@fluenceur/types';
import React from 'react';
import { useTranslation } from 'react-i18next';

interface AffiliatesListProps {
  payout: Payout | null;
}

export const AffiliatesList: React.FC<AffiliatesListProps> = ({ payout }) => {
  const { t } = useTranslation();

  if (!payout?.affiliatePayouts || payout.affiliatePayouts.length === 0) {
    return null;
  }

  // Create a map to match affiliate payouts with their details
  const getAffiliateDetails = (affiliateId: string): InvolvedAffiliate | undefined => {
    return payout.involvedAffiliates?.find((a) => a.id === affiliateId);
  };

  // Combine the data for display
  const affiliatesWithAmounts = payout.affiliatePayouts.map((ap: AffiliatePayoutSummary) => {
    const details = getAffiliateDetails(ap.affiliateId);
    return {
      ...ap,
      name: details?.name || t('payouts.confirmation.affiliates.unknownAffiliate'),
      avatar: details?.avatar,
    };
  });

  return (
    <Panel
      title={t('payouts.confirmation.affiliates.title', { count: payout.affiliatePayouts.length })}
      animation="fadeIn 0.5s ease-out 0.4s both"
      css={{
        '@keyframes fadeIn': {
          from: { opacity: 0, transform: 'translateY(10px)' },
          to: { opacity: 1, transform: 'translateY(0)' },
        },
      }}
    >
      <Stack gap={3}>
        {affiliatesWithAmounts.length <= 5 ? (
          // Show table for 5 or fewer affiliates
          <Table.Root size="sm">
            <Table.Header>
              <Table.Row>
                <Table.ColumnHeader>
                  {t('payouts.confirmation.affiliates.tableHeaders.affiliate')}
                </Table.ColumnHeader>
                <Table.ColumnHeader textAlign="right">
                  {t('payouts.confirmation.affiliates.tableHeaders.amount')}
                </Table.ColumnHeader>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {affiliatesWithAmounts.map((affiliate, index) => (
                <Table.Row
                  key={affiliate.affiliateId}
                  animation={`slideIn 0.3s ease-out ${0.1 * index}s both`}
                  css={{
                    '@keyframes slideIn': {
                      from: { opacity: 0, transform: 'translateX(-20px)' },
                      to: { opacity: 1, transform: 'translateX(0)' },
                    },
                  }}
                >
                  <Table.Cell>
                    <AffiliateInfo
                      name={affiliate.name}
                      avatarUrl={affiliate.avatar?.fileUrl}
                      programName={affiliate.programs?.[0]?.name || ''}
                      size="xs"
                    />
                  </Table.Cell>
                  <Table.Cell textAlign="right">
                    <Money
                      fontSize="sm"
                      value={affiliate.totalAmount.amount}
                      currency={affiliate.totalAmount.currencyCode}
                    />
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table.Root>
        ) : (
          // Show summary for more than 5 affiliates
          <>
            <Box>
              {affiliatesWithAmounts.slice(0, 3).map((affiliate, index) => (
                <Flex
                  key={affiliate.affiliateId}
                  justify="space-between"
                  align="center"
                  py={2}
                  animation={`slideIn 0.3s ease-out ${0.1 * index}s both`}
                  css={{
                    '@keyframes slideIn': {
                      from: { opacity: 0, transform: 'translateX(-20px)' },
                      to: { opacity: 1, transform: 'translateX(0)' },
                    },
                  }}
                >
                  <AffiliateInfo
                    name={affiliate.name}
                    avatarUrl={affiliate.avatar?.fileUrl}
                    programName={affiliate.programs?.[0]?.name || ''}
                    size="xs"
                  />
                  <Money
                    value={affiliate.totalAmount.amount}
                    currency={affiliate.totalAmount.currencyCode}
                  />
                </Flex>
              ))}
            </Box>
            <Text fontSize="sm" color="gray.600" textAlign="center" fontWeight="medium">
              {t('payouts.confirmation.affiliates.moreAffiliates', {
                count: affiliatesWithAmounts.length - 3,
              })}
            </Text>
          </>
        )}

        <Text fontSize="sm" color="gray.500" mt={2}>
          {t('payouts.confirmation.affiliates.transferNote')}
        </Text>
      </Stack>
    </Panel>
  );
};
